import PropTypes from "prop-types";
import React, { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { withTranslation } from "react-i18next";
import withRouter from "../../components/Common/withRouter";

//redux
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";

// Formik validation
import { useFormik } from "formik";
import * as Yup from "yup";

import {
  Alert,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
} from "reactstrap";

// actions

// import images
import { useLogin } from "../../api/auth";
import profile from "../../assets/images/profile-img.png";
import CustomHeader from "../../components/Auth/CustomHeader";
import {
  setAgreementLink,
  setOnboardingComplete,
  setSession,
} from "/src/store/actions";
import {
  selectIsAuthenticated,
  selectRole,
} from "../../store/auth/selectors";
import { forceEnglishLanguage } from "../../helpers/i18nLanguage";

const Login = (props) => {
  const { t } = props;
  //meta title
  const pathname = props?.router?.location?.pathname || "";
  const search = props?.router?.location?.search || "";
  const qp = useMemo(() => new URLSearchParams(search), [search]);
  const userTypeFromPath =
    pathname === "/login/client"
      ? "client"
      : pathname === "/login/worker"
      ? "worker"
      : null;
  const userType =
    props.userTypeOverride || userTypeFromPath || qp.get("userType") || "admin";
  const userTypeLabel =
    userType === "client"
      ? t("loginRoleClient", "Client")
      : userType === "worker"
      ? t("loginRoleWorker", "Worker")
      : t("loginRoleAdmin", "Admin");

  document.title = t("loginPageTitle", {
    role: userTypeLabel,
    defaultValue: "{{role}} Login | JobArk",
  });

  const dispatch = useDispatch();
  const mutation = useLogin();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const sessionRole = useSelector(selectRole);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (userType !== "worker") {
      forceEnglishLanguage();
    }
  }, [userType]);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (sessionRole === "worker") {
      props.router.navigate("/dashboard", { replace: true });
      return;
    }
    if (sessionRole === "client") {
      props.router.navigate("/dashboard", { replace: true });
      return;
    }
    if (sessionRole === "admin" || sessionRole === "subAdmin") {
      props.router.navigate("/dashboard", { replace: true });
    }
  }, [isAuthenticated, sessionRole, props.router]);

  const validation = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Yup.object({
      email: Yup.string().required(
        t("loginEmailRequired", "Please Enter Your Email")
      ),
      password: Yup.string().required(
        t("loginPasswordRequired", "Please Enter Your Password")
      ),
    }),
    onSubmit: (values) => {
      mutation.mutate(values, {
        onSuccess: (apiRes) => {
          const payload = apiRes?.data ?? apiRes;
          const token = payload?.token;
          const user = payload?.user;
          if (!token || !user) return;

          dispatch(setSession({ accessToken: token, user }));
          if (user.role === "client") {
            dispatch(
              setOnboardingComplete(user.hasCompletedOnBoarding ?? true)
            );
            dispatch(setAgreementLink(user.agreementLink ?? null));
            // if (!user.hasCompletedOnBoarding) {
            //   props.router.navigate("/client-pandadocs");
            //   return;
            // }
            props.router.navigate("/dashboard");
            return;
          }
          if (user.role === "worker") {
            props.router.navigate("/dashboard");
            return;
          }
          props.router.navigate("/");
        },
      });
    },
  });

  const LoginProperties = createSelector(
    (state) => state.Login,
    (login) => ({
      error: login.error,
    })
  );

  const { error } = useSelector(LoginProperties);

  return (
    <React.Fragment>
      <CustomHeader />
      <div className="account-pages my-5 pt-sm-5">
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="overflow-hidden">
                <div className="bg-primary">
                  <Row>
                    <Col xs={7}>
                      <div className="text-primary p-4">
                        <h5 className="text-white">
                          {userType === "admin"
                            ? t("loginSignIn", "Sign In")
                            : t("loginRoleSignIn", {
                                role: userTypeLabel,
                                defaultValue: "{{role}} Sign In",
                              })}
                        </h5>
                        <p className="text-white">
                          {t("loginSignInContinue", "Sign in to continue")}
                        </p>
                      </div>
                    </Col>
                    <Col className="col-5 align-self-end">
                      <img src={profile} alt="" className="img-fluid" />
                    </Col>
                  </Row>
                </div>
                <CardBody className="pt-0">
                  <div className="p-2">
                    <Form
                      className="form-horizontal"
                      onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                        return false;
                      }}
                    >
                      {error ? <Alert color="danger">{error}</Alert> : null}

                      <div className="mb-3">
                        <Label className="form-label">
                          {t("email", "Email")}
                        </Label>
                        <Input
                          name="email"
                          className="form-control"
                          placeholder={t(
                            "loginEmailPlaceholder",
                            "Enter email"
                          )}
                          type="email"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.email || ""}
                          invalid={
                            validation.touched.email && validation.errors.email
                              ? true
                              : false
                          }
                        />
                        {validation.touched.email && validation.errors.email ? (
                          <FormFeedback type="invalid">
                            {validation.errors.email}
                          </FormFeedback>
                        ) : null}
                      </div>

                      <div className="mb-3">
                        <Label className="form-label">
                          {t("loginPasswordLabel", "Password")}
                        </Label>
                        <div className="position-relative">
                          <Input
                            name="password"
                            autoComplete="off"
                            value={validation.values.password || ""}
                            type={showPassword ? "text" : "password"}
                            placeholder={t(
                              "loginPasswordPlaceholder",
                              "Enter Password"
                            )}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={
                              validation.touched.password &&
                              validation.errors.password
                                ? true
                                : false
                            }
                            style={{ paddingRight: "40px" }}
                          />
                          <button
                            type="button"
                            className="btn btn-link position-absolute"
                            style={{
                              right: "5px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              border: "none",
                              background: "none",
                              padding: "0 8px",
                              color: "#6c757d",
                              textDecoration: "none",
                            }}
                            onClick={() => setShowPassword(!showPassword)}
                            tabIndex={-1}
                          >
                            <i
                              className={
                                showPassword ? "mdi mdi-eye-off" : "mdi mdi-eye"
                              }
                            ></i>
                          </button>
                        </div>
                        {validation.touched.password &&
                        validation.errors.password ? (
                          <FormFeedback type="invalid">
                            {validation.errors.password}
                          </FormFeedback>
                        ) : null}
                      </div>

                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="customControlInline"
                        />
                        <label
                          className="form-check-label"
                          htmlFor="customControlInline"
                        >
                          {t("loginRememberMe", "Remember me")}
                        </label>
                      </div>

                      <div className="mt-3 d-grid">
                        <button
                          className="btn btn-secondary btn-block"
                          type="submit"
                          disabled={mutation.isPending}
                        >
                          {mutation.isPending
                            ? t("loginPleaseWait", "Please wait...")
                            : t("loginLogIn", "Log In")}
                        </button>
                      </div>

                      <div className="mt-4 text-center">
                        <Link to="/forgot-password" className="text-muted">
                          <i className="mdi mdi-lock me-1" />
                          {t("loginForgotPassword", "Forgot your password?")}
                        </Link>
                      </div>
                    </Form>
                  </div>
                </CardBody>
              </Card>
              <div className="mt-5 text-center">
                {userType === "client" && (
                  <p>
                    {t("loginNoAccount", "Don't have an account ?")}{" "}
                    <Link
                      to="/client-interest"
                      className="fw-medium text-primary"
                    >
                      {" "}
                      {t("loginOnboardAsClient", "Onboard as client")}{" "}
                    </Link>
                  </p>
                )}
                {userType === "worker" && (
                  <p>
                    {t("loginNoAccount", "Don't have an account ?")}{" "}
                    <Link
                      to="/work-interest"
                      className="fw-medium text-primary"
                    >
                      {" "}
                      {t("loginOnboardAsWorker", "On Board As Worker")}{" "}
                    </Link>
                  </p>
                )}
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default withRouter(withTranslation()(Login));

Login.propTypes = {
  history: PropTypes.object,
  userTypeOverride: PropTypes.string,
  t: PropTypes.func.isRequired,
};
